/**
 * An abstraction component that marries the theme-selection to the overpanel, to pull
 * some unnecessary logic out of the preview page. (ThemeSelection will likely only ever
 * be used in an overpanel.)
 */

import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'transfer/utils/p2p-connect'
import ThemeSelection from './theme-selection'
import Overpanel from 'react-components/overpanel'
import { formatAmount } from 'transfer/utils/currencyUtils'
import { changeNote } from 'transfer/actions/Actions'
import { getFlowType } from 'transfer/utils/url-utils'
import { changeThemeId } from 'transfer/actions/theme'

const callAll = (...fns) => (...args) => fns.forEach(fn => fn && fn(...args))

function ThemeSelectionOverpanel({
  handleCloseButton,
  show,
  displayName,
  formattedAmount,
  handleNextButton,
  handleChangeNote,
  handleRemoveButton,
  note,
  thumbnailUrl,
  savedThemeExists,
  themeIsMandatory = false,
  onSubmitTheme,
  onRemoveTheme,
}) {
  return (
    <Overpanel
      preset="FULL"
      show={show}
      handleClose={handleCloseButton}
      styleOverrides={{
        content: { padding: 0 },
        closeButton: { color: 'white' },
      }}
    >
      <ThemeSelection
        displayName={displayName}
        formattedAmount={formattedAmount}
        handleNextButton={callAll(
          handleNextButton,
          handleCloseButton,
          onSubmitTheme
        )}
        handleCancelButton={handleCloseButton}
        handleRemoveButton={callAll(
          handleRemoveButton,
          handleCloseButton,
          onRemoveTheme
        )}
        thumbnailUrl={thumbnailUrl}
        handleChangeNote={handleChangeNote}
        savedThemeExists={savedThemeExists}
        themeIsMandatory={themeIsMandatory}
        note={note}
      />
    </Overpanel>
  )
}

ThemeSelectionOverpanel.propTypes = {
  handleCloseButton: PropTypes.func,
  show: PropTypes.bool,
  displayName: PropTypes.string,
  formattedAmount: PropTypes.string,
  handleChangeNote: PropTypes.func,
  note: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  savedThemeExists: PropTypes.bool,
  themeIsMandatory: PropTypes.bool,
  handleNextButton: PropTypes.func,
  handleRemoveButton: PropTypes.func,
  onSubmitTheme: PropTypes.func,
  onRemoveTheme: PropTypes.func,
}

export default connect(
  ({ server, amount, note, theme }) => ({
    displayName: server.userFullName,
    formattedAmount: formatAmount(amount.amount, amount.currencyCode),
    note: note.note,
    thumbnailUrl: server.userThumbnailUrl,
    savedThemeExists: !!theme.themeId,
    themeIsMandatory: getFlowType() === 'gift', // theme is mandatory in gift flow
  }),
  dispatch => ({
    handleChangeNote: note => dispatch(changeNote(note)),
    handleNextButton: themeId => dispatch(changeThemeId(themeId)),
    handleRemoveButton: () => dispatch(changeThemeId('')),
  }),
  (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
  })
)(ThemeSelectionOverpanel)
